import {GridColDef} from '@mui/x-data-grid-pro';

import {Box} from '@mui/material';
import {t} from 'i18next';
import {useMemo} from 'react';
import {FCFileGridItem} from '../../../../../types/upload-new-eviidence/fcUploadFileTypes';
import {
  FCFileTransferState,
  getFileSize,
} from '../../../../../utils/fcUploadFileUtils';
import AurButton from '../../../../common/aur-button/AurButton';
import AurLoadingIndicator from '../../../../common/aur-loading-indicator/AurLoadingIndictor';

export enum FileGridFieldEnum {
  FILE_NAME,
  SIZE,
  STATUS,
  PROGRESS,
  ACTION,
}

const GRID_WIDTH = 1232;

type onClickFCRowAction = (row: FCFileGridItem) => void;

export const useFCUploadFileGridConfig = (
  onClickPendingFileActionButton: onClickFCRowAction,
  onClickUploadedFileActionButton: onClickFCRowAction
) => {
  const baseFileGridColumnConfigObj: Record<
    FileGridFieldEnum,
    GridColDef<FCFileGridItem>
  > = useMemo(
    () => ({
      [FileGridFieldEnum.FILE_NAME]: {
        field: 'displayName',
        headerName: t('fileCatalyst.fileCatalystGrid.fileName'),
        display: 'flex',
        flex: 571 / GRID_WIDTH,
        sortable: false,
        resizable: false,
        headerClassName: 'custom-header',
        cellClassName: 'file-grid-item-cell',
      },
      [FileGridFieldEnum.STATUS]: {
        field: 'status',
        headerName: t('fileCatalyst.fileCatalystGrid.status'),
        display: 'flex',
        flex: 100 / GRID_WIDTH,
        sortable: false,
        resizable: false,
        headerClassName: 'custom-header',
        cellClassName: 'file-grid-item-cell',
      },
      [FileGridFieldEnum.SIZE]: {
        field: 'size',
        headerName: t('fileCatalyst.fileCatalystGrid.size'),
        display: 'flex',
        flex: 69 / GRID_WIDTH,
        minWidth: 90,
        sortable: false,
        resizable: false,
        headerClassName: 'custom-header',
        cellClassName: 'file-grid-item-cell',
        valueFormatter: (_, row) => {
          return getFileSize(row?.size ?? 0);
        },
      },
      [FileGridFieldEnum.PROGRESS]: {
        field: 'progress',
        headerName: t('fileCatalyst.fileCatalystGrid.overallProgress'),
        display: 'flex',
        flex: 332 / GRID_WIDTH,
        sortable: false,
        resizable: false,
        headerClassName: 'custom-header',
        cellClassName: 'fc-progress-bar-custom-cell',
      },
      [FileGridFieldEnum.ACTION]: {
        field: 'cancelTransferOnline',
        headerName: '',
        display: 'flex',
        flex: 160 / GRID_WIDTH,
        minWidth: 175,
        sortable: false,
        resizable: false,
      },
    }),
    []
  );

  const pendingFileGridConfig: GridColDef<FCFileGridItem>[] = useMemo(
    () => [
      baseFileGridColumnConfigObj[FileGridFieldEnum.FILE_NAME],
      {
        ...baseFileGridColumnConfigObj[FileGridFieldEnum.STATUS],
        cellClassName: (params) => {
          if (params.row.status === FCFileTransferState.FAILED) {
            return 'file-grid-item-cell-error';
          } else {
            return 'file-grid-item-cell';
          }
        },
        valueFormatter: (_, row) => {
          return t(`fileCatalyst.fileCatalystGrid.${row.status}`);
        },
      },
      baseFileGridColumnConfigObj[FileGridFieldEnum.SIZE],
      {
        ...baseFileGridColumnConfigObj[FileGridFieldEnum.PROGRESS],
        renderCell: (params) => {
          return (
            <div>
              <div className={'progress-bar-cell-inprogress-file'}>
                <div
                  className={
                    params.row.status === FCFileTransferState.FAILED
                      ? 'progress-bar-failed-container'
                      : 'progress-bar-uploading-container'
                  }
                  style={{
                    width: `${params?.row?.progress ?? 0}%`,
                    transitionDuration:
                      (params?.row?.progress ?? 0) === 0 ? '0s' : '0.5s',
                  }}
                />
                <div className={'progress-bar-background-container'} />
              </div>
            </div>
          );
        },
      },
      {
        ...baseFileGridColumnConfigObj[FileGridFieldEnum.ACTION],
        renderCell: (params) => {
          const {isTranferCancelled, status} = params.row;

          return (
            <Box className={'grid-action-container'}>
              {isTranferCancelled ? (
                <AurLoadingIndicator
                  size={14}
                  className="cell-loader-control"
                />
              ) : null}
              <AurButton
                size={'Small'}
                type={'text'}
                label={
                  status === FCFileTransferState.FAILED
                    ? t('fileCatalyst.fileCatalystGrid.retryUpload')
                    : t('fileCatalyst.fileCatalystGrid.cancelUpload')
                }
                disabled={
                  isTranferCancelled || status === FCFileTransferState.COMPLETED
                }
                onClick={() => {
                  onClickPendingFileActionButton(params.row);
                }}
              />
            </Box>
          );
        },
      },
    ],
    [baseFileGridColumnConfigObj, onClickPendingFileActionButton]
  );

  const uploadedFileGridConfig: GridColDef<FCFileGridItem>[] = useMemo(
    () => [
      {
        ...baseFileGridColumnConfigObj[FileGridFieldEnum.FILE_NAME],
        flex: 671 / GRID_WIDTH,
      },
      baseFileGridColumnConfigObj[FileGridFieldEnum.SIZE],
      {
        ...baseFileGridColumnConfigObj[FileGridFieldEnum.PROGRESS],
        renderCell: () => {
          return (
            <div>
              <div className={'progress-bar-cell-uploaded-file'}>
                <div className={'progress-bar-master-container'} />
              </div>
            </div>
          );
        },
      },
      {
        ...baseFileGridColumnConfigObj[FileGridFieldEnum.ACTION],
        renderCell: (params) => {
          return (
            <Box className={'grid-action-container'}>
              <AurButton
                size={'Small'}
                type={'text'}
                label={t('fileCatalyst.fileCatalystGrid.removeFile')}
                onClick={() => {
                  onClickUploadedFileActionButton(params.row);
                }}
              />
            </Box>
          );
        },
      },
    ],
    [baseFileGridColumnConfigObj, onClickUploadedFileActionButton]
  );

  return {
    pendingFileGridConfig,
    uploadedFileGridConfig,
  };
};
